import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Braces, GitBranch, Info, ListChecks, Plus } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/select';
import { useNodeStore } from '@/stores/node-store';
import type { CustomNode, InputValue, NodeStruct } from '@/types/node-types';
import {
  getIfConfig,
  validateIfConfig,
  type ExpressionAst,
  type IfConfigValue,
  type IfMissingBehavior,
} from '@/pages/workflow/helper/functional-control-flow';
import {
  astToCondition,
  conditionToAst,
  describeCondition,
  fieldSuggestionsForSample,
  newRule,
  type Condition,
} from '@/pages/workflow/helper/switch-condition-builder';
import { previewIfSelection } from '@/pages/workflow/helper/expression-eval';
import {
  edgesForBranch,
  orphanedBranchEdges,
  resolveSwitchInputSample,
} from '@/pages/workflow/helper/switch-branch-sync';
import { ConditionEditor, DraftInput } from './switch-cases-editor';
import { JsonEditorField } from './json-editor-field';
import { cn } from '@/lib/utils';

/**
 * The If / Else panel (Phase 8b). One sentence with pickers:
 *
 *   If [condition] then → [Then branch], otherwise → [Otherwise branch].
 *   If the field is missing or empty, the Otherwise branch runs.
 *
 * The condition is the Switch's condition builder over the `value` root; the
 * JSON underneath is one Switch case. Renaming a branch renames its edges
 * through the store's branch sync, exactly as a Switch case rename does.
 */

const MISSING_ELSE = 'the Otherwise branch runs';
const MISSING_ERROR = 'stop the run';

interface IfConditionEditorProps {
  nodeId: string;
  struct: NodeStruct;
  disabled?: boolean;
  info?: string;
  onChange: (fieldId: string, value: InputValue) => void;
}

export function IfConditionEditor({
  nodeId,
  struct,
  disabled = false,
  info,
  onChange,
}: IfConditionEditorProps) {
  const {
    node,
    nodes,
    edges,
    renameSwitchBranch,
    onEdgesDelete,
    convertIfToSwitch,
  } = useNodeStore(
    useShallow((state) => ({
      node: state.nodes.find((n) => n.id === nodeId) as CustomNode | undefined,
      nodes: state.nodes,
      edges: state.edges,
      renameSwitchBranch: state.renameSwitchBranch,
      onEdgesDelete: state.onEdgesDelete,
      convertIfToSwitch: state.convertIfToSwitch,
    })),
  );
  const [mode, setMode] = useState<'builder' | 'json'>('builder');
  const [confirmConvert, setConfirmConvert] = useState(false);

  const config = useMemo(() => getIfConfig(struct), [struct]);
  const condition = useMemo<Condition>(
    () => (config.condition ? astToCondition(config.condition) : newRule()),
    [config.condition],
  );
  const errors = useMemo(() => validateIfConfig(config), [config]);
  const errorFor = (field: string): string | null =>
    errors.find((error) => error.path === field)?.message ?? null;

  const sample = useMemo(
    () => (node ? resolveSwitchInputSample(node, nodes, edges) : undefined),
    [node, nodes, edges],
  );
  const suggestions = useMemo(
    () => fieldSuggestionsForSample(sample),
    [sample],
  );
  const preview = useMemo(
    () =>
      previewIfSelection(config, sample, () => describeCondition(condition)),
    [config, sample, condition],
  );
  const orphaned = useMemo(
    () => orphanedBranchEdges(struct, edges, nodeId),
    [struct, edges, nodeId],
  );
  const upstreamName = useMemo(() => {
    const valuePortId = struct.params?.find((p) => p.field_id === 'value')
      ?.bindable_config?.input_port?.id;
    const incoming = edges.find(
      (edge) => edge.target === nodeId && edge.targetHandle === valuePortId,
    );
    const source = nodes.find((n) => n.id === incoming?.source);
    return (
      source?.data?.struct?.user_config_name ||
      source?.data?.struct?.display_name ||
      null
    );
  }, [struct.params, edges, nodes, nodeId]);

  const commitCondition = (next: Condition) =>
    onChange('condition', conditionToAst(next) as unknown as InputValue);

  const renameBranch = (field: 'then_label' | 'else_label', next: string) => {
    const trimmed = next.trim();
    const current =
      field === 'then_label' ? config.thenLabel : config.elseLabel;
    if (!trimmed || trimmed === current) return;
    const candidate: IfConfigValue = {
      ...config,
      [field === 'then_label' ? 'thenLabel' : 'elseLabel']: trimmed,
    };
    if (validateIfConfig(candidate).every((error) => error.path !== field)) {
      renameSwitchBranch(nodeId, current, trimmed);
    }
    onChange(field, trimmed);
  };

  const canCombine = condition.kind === 'rule' || condition.kind === 'always';
  const wrap = (op: 'and' | 'or') =>
    commitCondition({
      kind: 'group',
      op,
      items: [condition.kind === 'always' ? newRule() : condition, newRule()],
    });

  const branchRow = (
    which: 'then' | 'else',
    label: string,
    field: 'then_label' | 'else_label',
  ) => {
    const connected = edgesForBranch(edges, nodeId, label).length;
    const error = errorFor(field);
    return (
      <div
        className='flex items-center gap-2'
        data-testid={`if-branch-${which}`}
      >
        <span className='w-20 shrink-0 text-xs text-muted-foreground'>
          {which === 'then' ? 'then →' : 'otherwise →'}
        </span>
        <DraftInput
          value={label}
          onCommit={(next) => renameBranch(field, next)}
          disabled={disabled}
          placeholder={which}
          aria-label={
            which === 'then' ? 'Then branch name' : 'Otherwise branch name'
          }
          data-testid={`if-${which}-label`}
          className={cn('font-medium flex-1', error && 'border-red-500')}
        />
        <span className='shrink-0 text-[11px] text-muted-foreground'>
          {connected > 0
            ? `${connected} connection${connected === 1 ? '' : 's'}`
            : 'drag from the handle'}
        </span>
      </div>
    );
  };

  return (
    <div className='w-full mb-4 px-3' data-testid='if-condition-editor'>
      <div className='flex items-center justify-between gap-2 mb-1 min-h-[36px]'>
        <div className='flex items-center gap-1'>
          <Label className='text-sm label-required'>If</Label>
          {info && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className='h-4 w-4 text-muted-foreground cursor-help' />
              </TooltipTrigger>
              <TooltipContent>
                <p className='max-w-xs'>{info}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-7 px-2 text-xs'
          data-testid='if-mode-toggle'
          onClick={() => setMode(mode === 'builder' ? 'json' : 'builder')}
        >
          {mode === 'builder' ? (
            <>
              <Braces className='size-3.5' /> Edit as JSON
            </>
          ) : (
            <>
              <ListChecks className='size-3.5' /> Edit as fields
            </>
          )}
        </Button>
      </div>

      {preview.kind !== 'unknown' && (
        <div
          className={cn(
            'rounded-md px-2 py-1.5 mb-2 text-xs',
            preview.kind === 'fail'
              ? 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
              : 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300',
          )}
          data-testid='if-preview'
        >
          {preview.kind === 'fail'
            ? `With the last value${upstreamName ? ` from ${upstreamName}` : ''}, this If would fail the run: ${preview.message}`
            : `With the last value${upstreamName ? ` from ${upstreamName}` : ''}, this If would take ${preview.kind === 'then' ? 'Then' : 'Otherwise'} ("${preview.kind === 'then' ? config.thenLabel : config.elseLabel}") because ${preview.because}.`}
        </div>
      )}

      {mode === 'json' ? (
        <JsonEditorField
          value={{
            condition: config.condition,
            then_label: config.thenLabel,
            else_label: config.elseLabel,
            missing_behavior: config.missingBehavior,
          }}
          rows={12}
          expectedShape='object'
          disabled={disabled}
          aria-label='If configuration as JSON'
          data-testid='if-json'
          placeholder='{"condition": {"op": "eq", "left": {"op": "path", "path": ["value", "status"]}, "right": {"op": "literal", "value": "ok"}}, "then_label": "then", "else_label": "else", "missing_behavior": "else"}'
          validate={(parsed) => {
            const record = parsed as Record<string, unknown>;
            const candidate: IfConfigValue = {
              condition:
                record.condition && typeof record.condition === 'object'
                  ? (record.condition as ExpressionAst)
                  : null,
              thenLabel:
                typeof record.then_label === 'string' &&
                record.then_label.trim()
                  ? record.then_label.trim()
                  : 'then',
              elseLabel:
                typeof record.else_label === 'string' &&
                record.else_label.trim()
                  ? record.else_label.trim()
                  : 'else',
              missingBehavior:
                record.missing_behavior === 'error' ? 'error' : 'else',
            };
            return validateIfConfig(candidate)[0]?.message ?? null;
          }}
          onCommit={(parsed) => {
            const record = parsed as Record<string, unknown>;
            const nextThen =
              typeof record.then_label === 'string' && record.then_label.trim()
                ? record.then_label.trim()
                : 'then';
            const nextElse =
              typeof record.else_label === 'string' && record.else_label.trim()
                ? record.else_label.trim()
                : 'else';
            if (nextThen !== config.thenLabel) {
              renameSwitchBranch(nodeId, config.thenLabel, nextThen);
            }
            if (nextElse !== config.elseLabel) {
              renameSwitchBranch(nodeId, config.elseLabel, nextElse);
            }
            onChange('condition', record.condition as InputValue);
            onChange('then_label', nextThen);
            onChange('else_label', nextElse);
            onChange(
              'missing_behavior',
              record.missing_behavior === 'error' ? 'error' : 'else',
            );
          }}
        />
      ) : (
        <div className='space-y-2'>
          <div
            className={cn(
              'rounded-lg border p-2.5 space-y-2 bg-card',
              errorFor('condition') && 'border-red-400',
            )}
          >
            <ConditionEditor
              condition={condition}
              suggestions={suggestions}
              disabled={disabled}
              depth={0}
              hideMissingGuard
              onChange={commitCondition}
            />
            {canCombine && !disabled && (
              <div className='flex gap-1'>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='h-6 px-2 text-xs'
                  onClick={() => wrap('and')}
                >
                  <Plus className='size-3' /> and also
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='h-6 px-2 text-xs'
                  onClick={() => wrap('or')}
                >
                  <Plus className='size-3' /> or instead
                </Button>
              </div>
            )}
          </div>

          {branchRow('then', config.thenLabel, 'then_label')}
          {branchRow('else', config.elseLabel, 'else_label')}

          <div className='flex items-center gap-2'>
            <span className='w-20 shrink-0 text-xs text-muted-foreground'>
              if missing
            </span>
            <div className='flex-1' data-testid='if-missing-behavior'>
              <Select
                options={[MISSING_ELSE, MISSING_ERROR]}
                selected={
                  config.missingBehavior === 'error'
                    ? MISSING_ERROR
                    : MISSING_ELSE
                }
                allowAllOption={false}
                disabled={disabled}
                className='w-full h-auto'
                aria-label='If the field is missing or empty'
                onSelect={(choice) => {
                  const next: IfMissingBehavior =
                    choice === MISSING_ERROR ? 'error' : 'else';
                  if (next !== config.missingBehavior) {
                    onChange('missing_behavior', next);
                  }
                }}
              />
            </div>
          </div>
          <p className='text-[11px] text-muted-foreground'>
            {config.missingBehavior === 'else'
              ? 'If the field is missing or empty, or cannot be compared, the Otherwise branch runs.'
              : 'If the field is missing or empty, the run stops with an error (like a Switch).'}
          </p>
        </div>
      )}

      {errors.length > 0 && (
        <ul className='mt-2 space-y-0.5' role='alert'>
          {errors.slice(0, 3).map((error, i) => (
            <li key={i} className='text-xs text-red-500'>
              {error.message}
            </li>
          ))}
        </ul>
      )}

      {orphaned.length > 0 && (
        <div className='mt-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs flex flex-wrap items-center gap-2'>
          <span className='flex-1'>
            {orphaned.length} connection{orphaned.length === 1 ? '' : 's'} use a
            branch name that no longer exists (
            {[...new Set(orphaned.map((e) => e.data?.branch_label))].join(', ')}
            ). They will be dropped when you save.
          </span>
          {!disabled && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='h-6 px-2 text-xs'
              onClick={() => onEdgesDelete(orphaned)}
            >
              Remove now
            </Button>
          )}
        </div>
      )}

      {mode === 'builder' && (
        <p className='mt-2 text-[11px] text-muted-foreground'>
          If {describeCondition(condition)} → {config.thenLabel}, otherwise →{' '}
          {config.elseLabel}.
        </p>
      )}

      {!disabled && (
        <div className='mt-3 flex flex-wrap items-center gap-2'>
          {confirmConvert ? (
            <div
              className='flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-2 text-xs'
              role='alert'
            >
              <span className='flex-1'>
                Turn this If into a Switch with one case ({config.thenLabel})
                and a default branch ({config.elseLabel})? The connections move
                with it.
              </span>
              <Button
                type='button'
                size='sm'
                className='h-6 px-2 text-xs'
                data-testid='if-convert-confirm'
                onClick={() => {
                  setConfirmConvert(false);
                  convertIfToSwitch(nodeId);
                }}
              >
                Convert
              </Button>
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='h-6 px-2 text-xs'
                onClick={() => setConfirmConvert(false)}
              >
                Keep the If
              </Button>
            </div>
          ) : (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 px-2 text-xs text-muted-foreground'
              data-testid='if-convert-to-switch'
              title='Need a third branch? Convert this If to a Switch; nothing is lost.'
              onClick={() => setConfirmConvert(true)}
            >
              <GitBranch className='size-3.5' /> Convert to Switch
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
